import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import { Alert } from '@design-system/components/alert/alert';
import { Button, TextButton } from '@design-system/components/button/button';
import { Drawer } from '@design-system/components/modal/sideModal';
import { ProgressBar } from '@design-system/components/progressBar/progressBar';
import { Typography } from '@design-system/components/typography/typography';

import {
  FunnelCard,
  InsightsDrawerViewModel,
  NotRunningCard,
  useInsightsDrawerViewModel,
} from '../../view-model/AutomationInsights.viewModel';
import * as styles from '../Automations.view.css';

/** The DS drawer fixes its paper at 670 px with visible overflow (a global
 *  style), which clips this 846 px design and kills scrolling. `&&` wins the
 *  specificity fight without `!important`. Shared with the details panel. */
export const WIDE_DRAWER_SX = {
  zIndex: 1300,
  '&& .MuiDrawer-paper': {
    width: 'min(846px, 100vw)',
    maxWidth: '100vw',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
} as const;

type AutomationInsightsDrawerProps = {
  creatorId: number;
  isOpen: boolean;
  onClose: () => void;
  /** "Set it up" on a not-running card: the caller closes the drawer and opens the details modal (GRW-741). */
  onSetUp?: (automationType: string) => void;
};

export const AutomationInsightsDrawer = ({
  creatorId,
  isOpen,
  onClose,
  onSetUp,
}: AutomationInsightsDrawerProps) => (
  <Drawer isOpen={isOpen} onClose={onClose} drawerSx={WIDE_DRAWER_SX}>
    <div className={styles.drawer} data-testid="insights-drawer">
      <div className={styles.drawerHeader}>
        <div>
          <Typography as="h2" variant="headline" size="large">
            Insights
          </Typography>
          <Typography
            variant="body"
            size="medium"
            className={styles.drawerSubtitle}
          >
            What your automations have brought in since you turned them on
          </Typography>
        </div>
        <TextButton onClick={onClose} size="medium">
          Close
        </TextButton>
      </div>
      {isOpen ? <DrawerBody creatorId={creatorId} onSetUp={onSetUp} /> : null}
    </div>
  </Drawer>
);

const DrawerBody = ({
  creatorId,
  onSetUp,
}: Pick<AutomationInsightsDrawerProps, 'creatorId' | 'onSetUp'>) => {
  const vm = useInsightsDrawerViewModel(creatorId);

  if (vm.type === 'not-fetched' || vm.type === 'fetching') {
    return (
      <div
        className={styles.skeleton}
        aria-busy
        data-testid="insights-loading"
      />
    );
  }
  if (vm.type === 'fetch-error') {
    return (
      <Alert
        variant="error"
        details={vm.value.message}
        action={{ label: 'Try again', onClick: () => void vm.value.retry() }}
      />
    );
  }
  return <DrawerContent vm={vm.value} onSetUp={onSetUp} />;
};

const DrawerContent = ({
  vm,
  onSetUp,
}: {
  vm: InsightsDrawerViewModel;
  onSetUp?: (automationType: string) => void;
}) => (
  <div className={styles.drawerBody}>
    <section className={styles.summaryCard} aria-label="Conversions">
      <span className={styles.summaryLabel}>Conversions</span>
      <span className={styles.summaryValue}>{vm.totalConversions}</span>
      <span className={styles.summaryCaption}>{vm.totalCaption}</span>
    </section>

    {vm.running.length > 0 ? (
      <section className={styles.drawerSection} aria-label="Running">
        <span className={styles.drawerSectionTitle}>Running</span>
        {vm.running.map((card) => (
          <FunnelCardView
            key={card.automationType}
            card={card}
            adminTools={vm.adminTools}
          />
        ))}
      </section>
    ) : null}

    {vm.notRunning.length > 0 ? (
      <section className={styles.drawerSection} aria-label="Not running">
        <span className={styles.drawerSectionTitle}>Not running</span>
        {vm.notRunning.map((card) => (
          <NotRunningCardView
            key={card.automationType}
            card={card}
            onSetUp={onSetUp}
          />
        ))}
      </section>
    ) : null}

    <p className={styles.drawerFooter}>
      <FontAwesomeIcon icon={faCircleInfo} /> Updated every morning. A
      conversion is counted when a member who received one of these messages
      goes on to buy, subscribe, switch plan or come back.
    </p>
  </div>
);

const FunnelCardView = ({
  card,
  adminTools,
}: {
  card: FunnelCard;
  adminTools: boolean;
}) => (
  <article
    className={styles.funnelCard}
    data-testid={`funnel-${card.automationType}`}
  >
    <div className={styles.funnelHeader}>
      <div className={styles.funnelTitle}>
        <span className={styles.smallIconTile} aria-hidden>
          <FontAwesomeIcon icon={card.icon} />
        </span>
        <span className={styles.funnelName}>{card.displayName}</span>
      </div>
      <div className={styles.funnelHeadline}>
        <span className={styles.funnelHeadlineValue}>
          {card.headline.value}
        </span>
        <span className={styles.funnelHeadlineLabel}>
          {card.headline.label}
        </span>
      </div>
    </div>
    <div className={styles.funnelRows}>
      {card.rows.map((row) => (
        <div className={styles.funnelRow} key={row.key}>
          <div className={styles.funnelRowLabel}>
            <span>{row.label}</span>
            {row.note ? (
              <span className={styles.funnelRowNote}>{row.note}</span>
            ) : null}
          </div>
          <ProgressBar
            value={row.share}
            color={row.color}
            ariaLabel={row.label}
          />
          <div className={styles.funnelRowNumbers}>
            <span className={styles.funnelRowCount}>{row.count}</span>
            {row.percent ? (
              <span className={styles.funnelRowPercent}>{row.percent}</span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
    {adminTools ? (
      <details className={styles.funnelDetails}>
        <summary>Details</summary>
        <div className={styles.funnelDetailsBody}>
          <span>Closed enrollments: {card.details.closedTotal}</span>
          {card.details.closeReasons.map((reason) => (
            <span key={reason.key} className={styles.muted}>
              {reason.label}: {reason.count}
            </span>
          ))}
          {card.details.drainingLine ? (
            <span className={styles.muted}>{card.details.drainingLine}</span>
          ) : null}
          {card.details.steps.map((step) => (
            <span key={step.key} className={styles.muted}>
              {step.label}: sent {step.sent}, opens {step.opens}, clicks{' '}
              {step.clicks}
            </span>
          ))}
        </div>
      </details>
    ) : null}
  </article>
);

const NotRunningCardView = ({
  card,
  onSetUp,
}: {
  card: NotRunningCard;
  onSetUp?: (automationType: string) => void;
}) => (
  <article
    className={styles.notRunningCard}
    data-testid={`not-running-${card.automationType}`}
  >
    <div className={styles.funnelHeader}>
      <div className={styles.funnelTitle}>
        <span className={styles.smallIconTile} aria-hidden>
          <FontAwesomeIcon icon={card.icon} />
        </span>
        <span className={styles.funnelName}>{card.displayName}</span>
      </div>
      {card.needsSetup ? (
        <span className={styles.badgeNeedsSetup}>
          <span className={styles.badgeDot} aria-hidden />
          Needs setup
        </span>
      ) : null}
    </div>
    <div className={styles.notRunningBody}>
      <span className={styles.notRunningText}>{card.description}</span>
      <Button
        color="neutral"
        size="medium"
        onClick={() => onSetUp?.(card.automationType)}
        testId={`set-up-${card.automationType}`}
      >
        Set it up
      </Button>
    </div>
  </article>
);
